import { readonly, ref } from 'vue'
import { postToDevice } from './deviceBridge'

/**
 * How strong the tablet's Wi-Fi is, for the rail.
 *
 * A page cannot read signal strength; DockAutomation Tablet Browser can, and
 * reports it every few seconds and on every change (see deviceBridge.ts).
 * This holds the last report so a driver can see a weak spot before it
 * becomes a drop.
 *
 * It is a HINT. Whether the tablet is online is decided by real requests
 * reaching the server (useSyncQueue), never by this: strong Wi-Fi with a dead
 * server behind it is still offline, and a queue that is draining is online
 * whatever the bars say.
 *
 * `null` is the honest start and what a desktop stays at: no app, no report,
 * and the rail shows no Wi-Fi cell at all.
 */

export type WifiLevel = 'strong' | 'good' | 'weak' | 'poor'

export type WifiReport = {
  connected: boolean
  /** The network's name, or '' when the app could not read it. */
  ssid: string
  /** Signal strength in dBm (-50 strong, -80 barely holding), or null when unread. */
  rssi: number | null
  /** The tablet has confirmed the internet answers through this Wi-Fi. */
  internet: boolean
}

/**
 * Where one level ends and the next begins, in dBm. The usual thresholds for
 * warehouse Wi-Fi: -67 is the floor most access-point layouts are designed to
 * and -75 is where retries and drops begin. Retune the dock HERE, not in the
 * component.
 */
export const WIFI_THRESHOLDS = { strong: -60, good: -67, weak: -75 } as const

export function wifiLevel(rssi: number): WifiLevel {
  if (rssi >= WIFI_THRESHOLDS.strong) return 'strong'
  if (rssi >= WIFI_THRESHOLDS.good) return 'good'
  if (rssi >= WIFI_THRESHOLDS.weak) return 'weak'
  return 'poor'
}

export type WifiDisplay = {
  /** How many of the four bars are lit. */
  bars: 0 | 1 | 2 | 3 | 4
  /** The one word beside them. */
  word: string
  /** The line underneath. */
  sub: string
  /** The message the colour carries, as everywhere else on the rail. */
  tone: 'success' | 'warning' | 'error'
}

const LEVELS: Record<WifiLevel, Pick<WifiDisplay, 'bars' | 'word' | 'tone'>> = {
  strong: { bars: 4, word: 'Strong', tone: 'success' },
  good: { bars: 3, word: 'Good', tone: 'success' },
  weak: { bars: 2, word: 'Weak', tone: 'warning' },
  poor: { bars: 1, word: 'Poor', tone: 'error' },
}

/** What the rail shows for one report. No number: the driver reads bars and a word. */
export function wifiDisplay(report: WifiReport): WifiDisplay {
  if (!report.connected) return { bars: 0, word: 'No Wi-Fi', sub: 'Not Connected', tone: 'error' }
  // Joined but unread: say it is there without claiming a strength.
  const level = report.rssi === null
    ? { bars: 0 as const, word: 'On', tone: 'success' as const }
    : LEVELS[wifiLevel(report.rssi)]
  if (!report.internet) {
    // The bars are true and still worth seeing; the words say what is wrong.
    return { ...level, sub: 'No Internet', tone: level.tone === 'error' ? 'error' : 'warning' }
  }
  return { ...level, sub: report.ssid || 'Connected' }
}

const report = ref<WifiReport | null>(null)

export function reportWifi(next: WifiReport): void {
  report.value = next
}

/** Ask the app for a reading now, so the cell is not blank until the next one is due. */
export function askWifi(): boolean {
  return postToDevice({ command: 'wifi-status' })
}

/** For tests. */
export function resetWifi(): void {
  report.value = null
}

export function useWifiSignal() {
  return readonly(report)
}
